import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  aiIdPrefix?: string;
  children: ReactNode;
}

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  default: 'bg-[#0055cc] text-white border border-[#003399] hover:bg-[#0044bb]',
  secondary: 'bg-[#f5f5f5] text-[#333] border border-[#ccc] hover:bg-[#e6e6e6]',
  ghost: 'bg-transparent text-[#0055cc] hover:bg-transparent hover:underline',
  danger: 'bg-[#cc0000] text-white border border-[#990000] hover:bg-[#b30000]',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
};

export function Button({
  variant = 'default',
  size = 'md',
  loading = false,
  disabled,
  aiIdPrefix,
  className = '',
  children,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      className={[
        'inline-flex items-center justify-center gap-2 font-bold transition-none',
        'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
        variantClasses[variant],
        sizeClasses[size],
        isDisabled ? 'cursor-default opacity-50' : 'cursor-pointer',
        className,
      ].join(' ')}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
          data-ai-id={aiIdPrefix ? `${aiIdPrefix}-spinner` : undefined}
        />
      ) : null}
      <span data-ai-id={aiIdPrefix ? `${aiIdPrefix}-label` : undefined}>{children}</span>
    </button>
  );
}
